import clsx from 'clsx';
import Image from 'next/image';
import { useState } from 'react';
import { getFileUrl } from '@/app/(main)/gallery/utils/getFileUrl';
import { HighlightedText } from '@/app/components/HighlightedText';
import { galleryFileTypes } from '@/types/common';
import type { GlobalSearchGalleryItem } from '@/types/globalSearchApiTypes';
import { SearchItemTitle } from '../SearchItemTitle';
import { SearchItemWrapper } from '../SearchItemWrapper';
import LoaderSmall from '/public/icons/loader-small.svg';
import NoImageIcon from '/public/icons/no-image.svg';
import PdfFileIcon from '/public/icons/pdf-file.svg';

interface GalleryItemProps {
  item: GlobalSearchGalleryItem;
}

export const GalleryItem = ({ item }: GalleryItemProps) => {
  const [imageLoad, setImageLoad] = useState({
    isLoading: item.type === galleryFileTypes.IMAGE,
    isError: false,
  });
  const onLoadImageFailed = () => {
    setImageLoad({
      isLoading: false,
      isError: true,
    });
  };

  const onLoadImageSuccess = () => {
    setImageLoad({
      isLoading: false,
      isError: false,
    });
  };

  return (
    <SearchItemWrapper href={item.redirect_url}>
      <div className="flex items-center gap-2">
        {imageLoad.isLoading ? (
          <div className="relative w-[28px] h-[28px]">
            <div className="absolute inset-0 flex items-center justify-center z-20 bg-gray-800 bg-opacity-25 rounded-lg">
              <LoaderSmall className="w-5 h-5 text-white animate-spin" />
            </div>
          </div>
        ) : null}
        {imageLoad.isError ? (
          <div className="flex items-start justify-start h-[32px] w-[32px]">
            <NoImageIcon className="w-8 h-8 text-red-500" />
          </div>
        ) : null}
        {imageLoad.isError ? null : item.type === galleryFileTypes.IMAGE ? (
          <Image
            src={getFileUrl(item.logo) ?? ''}
            alt={item.title}
            width={32}
            height={32}
            className={clsx(
              'rounded-[2px] object-cover self-start',
              imageLoad.isLoading ? 'w-0 h-0' : 'w-[32px] h-[32px] min-w-[32px] min-h-[32px]'
            )}
            onError={onLoadImageFailed}
            onLoad={onLoadImageSuccess}
          />
        ) : (
          <PdfFileIcon className="w-[32px] h-[32px] self-start" />
        )}
        <div className="flex flex-col gap-1 max-md:min-w-0">
          <SearchItemTitle>{item.title}</SearchItemTitle>
          <div className="flex gap-1 max-md:flex-wrap">
            {item.caption && (
              <p className="text-body-small text-black !leading-[140%]">
                <span className="text-body-small text-grey-700 pr-[2px]">Caption:</span>
                <HighlightedText>{item.caption}</HighlightedText>;
              </p>
            )}
            <p className="text-body-small text-grey-700 !leading-[140%]">
              <HighlightedText>{item.description}</HighlightedText>
            </p>
          </div>
        </div>
      </div>
    </SearchItemWrapper>
  );
};
